import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import StickyTableResponsive from '../components/common/StickyTableResponsive';
import menuVisibilityService from '../services/menuVisibilityService';
import { MATRIX_USER_ROLES, getRoleLabel } from '../config/userRoles';
import { notify } from '../components/common/AppToast';
import { AppButton, AppSpinner } from '../components/ui';
import '../styles/kkp-planning-page.scss';

/** Matrice rôle × page : les rôles personnalisés viennent de l’API. */
const MenuVisibilityAdminPage = () => {
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleLabels, setRoleLabels] = useState(() => (
    Object.fromEntries(MATRIX_USER_ROLES.map((r) => [r.value, r.label]))
  ));
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await menuVisibilityService.getAdminMatrix();
      setCatalog(data.catalog ?? []);
      setRoles(data.roles ?? MATRIX_USER_ROLES.map((r) => r.value));
      const meta = Array.isArray(data.roles_meta) ? data.roles_meta : [];
      setRoleLabels(Object.fromEntries(
        (meta.length ? meta : MATRIX_USER_ROLES).map((r) => [r.value, r.label]),
      ));
      setMatrix(data.matrix ?? {});
    } catch (err) {
      notify.error(err.message || 'Impossible de charger la matrice');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleCollapsed = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setCell = (role, key, visible) => {
    setMatrix((prev) => {
      const nextRole = { ...(prev[role] ?? {}), [key]: visible };
      if (visible) {
        const byKey = Object.fromEntries(catalog.map((item) => [item.key, item]));
        let parent = byKey[key]?.parent ?? null;
        while (parent) {
          nextRole[parent] = true;
          parent = byKey[parent]?.parent ?? null;
        }
      }
      return { ...prev, [role]: nextRole };
    });
  };

  const setRowForAllRoles = (key, visible) => {
    setMatrix((prev) => {
      const next = { ...prev };
      for (const role of roles) {
        next[role] = { ...(next[role] ?? {}), [key]: visible };
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await menuVisibilityService.saveAdminMatrix(matrix);
      setMatrix(data.matrix ?? matrix);
      notify.success('Matrice enregistrée. Les utilisateurs verront les changements à leur prochaine navigation.');
      await menuVisibilityService.load(true);
    } catch (err) {
      notify.error(err.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const tree = useMemo(() => {
    const byParent = {};
    for (const item of catalog) {
      const parent = item.parent ?? '__root__';
      if (!byParent[parent]) byParent[parent] = [];
      byParent[parent].push(item);
    }
    return byParent;
  }, [catalog]);

  const renderRows = (parentKey, depth = 0) => {
    const items = tree[parentKey ?? '__root__'] ?? [];
    const rows = [];

    for (const item of items) {
      const children = tree[item.key] ?? [];
      const hasChildren = children.length > 0;
      const isCollapsed = collapsed[item.key];
      const indent = depth * 18;

      rows.push(
        <tr key={item.key} className={item.type === 'section' ? 'table-light' : ''}>
          <td style={{ paddingLeft: `${12 + indent}px` }}>
            <div className="d-flex align-items-center gap-2">
              {hasChildren ? (
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 text-secondary"
                  onClick={() => toggleCollapsed(item.key)}
                  aria-label={isCollapsed ? 'Déplier' : 'Replier'}
                >
                  <Icon icon={isCollapsed ? 'mdi:chevron-right' : 'mdi:chevron-down'} width={18} />
                </button>
              ) : (
                <span style={{ width: 18 }} />
              )}
              <span className={`badge ${
                item.type === 'section' ? 'bg-primary' : item.type === 'menu' ? 'bg-info' : 'bg-secondary'
              }`}>
                {item.type}
              </span>
              <span className={item.type === 'section' ? 'fw-semibold' : ''}>{item.label}</span>
              <code className="text-xs text-secondary-light d-none d-xl-inline">{item.key}</code>
            </div>
          </td>
          {roles.map((role) => (
            <td key={`${item.key}-${role}`} className="text-center">
              <input
                type="checkbox"
                className="form-check-input"
                checked={Boolean(matrix[role]?.[item.key])}
                onChange={(e) => setCell(role, item.key, e.target.checked)}
                title={`${roleLabels[role] ?? getRoleLabel(role)} — ${item.label}`}
              />
            </td>
          ))}
          <td className="text-center text-nowrap">
            <button
              type="button"
              className="btn btn-sm btn-outline-success py-0 px-1 me-1"
              title="Tout cocher"
              onClick={() => setRowForAllRoles(item.key, true)}
            >
              <Icon icon="mdi:check-all" width={14} />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary py-0 px-1"
              title="Tout décocher"
              onClick={() => setRowForAllRoles(item.key, false)}
            >
              <Icon icon="mdi:close" width={14} />
            </button>
          </td>
        </tr>,
      );

      if (hasChildren && !isCollapsed) {
        rows.push(...renderRows(item.key, depth + 1));
      }
    }

    return rows;
  };

  return (
    <MasterLayout>
      <Breadcrumb
        title="Droits des pages"
        subtitle="Autorisations de consultation par rôle (sections, menus et pages)"
        actions={(
          <AppButton
            size="sm"
            variant="success"
            icon="mdi:content-save-outline"
            loading={saving}
            onClick={handleSave}
            disabled={loading}
          >
            Enregistrer
          </AppButton>
        )}
      />

      <div className="card kkp-planning-results-card menu-visibility-matrix">
        <div className="card-body p-0">
          {loading ? (
            <AppSpinner />
          ) : (
            <StickyTableResponsive className="menu-visibility-matrix__table-wrap">
              <table className="table table-hover table-sm mb-0 align-middle kkp-iti-list-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 280 }}>Élément</th>
                    {roles.map((role) => (
                      <th key={role} className="text-center" style={{ minWidth: 90 }}>
                        <span className="d-block text-xs">{roleLabels[role] ?? getRoleLabel(role)}</span>
                      </th>
                    ))}
                    <th className="text-center" style={{ width: 80 }}>Ligne</th>
                  </tr>
                </thead>
                <tbody>
                  {renderRows(null)}
                </tbody>
              </table>
            </StickyTableResponsive>
          )}
        </div>
      </div>
    </MasterLayout>
  );
};

export default MenuVisibilityAdminPage;
